/**
 * Stripe Payment Integration
 * Uses Stripe Payment Links per country (EG, AE, SA)
 */

export interface StripePlanLinks {
  monthly: string
  yearly: string
}

export interface StripeCountryPlans {
  starter: StripePlanLinks
  business: StripePlanLinks // Growth
  pro: StripePlanLinks // Scale
}

// ─── STRIPE PAYMENT LINKS PER COUNTRY ──────────────────────────────────────
export const STRIPE_PAYMENT_LINKS: Record<string, StripeCountryPlans> = {
  // Egypt (EGP)
  eg: {
    starter: {
      monthly: 'https://buy.stripe.com/14A6oJeAm1So9K49sP57W03',
      yearly: 'https://buy.stripe.com/6oU6oJ2RE9kQg8sfRd57W04',
    },
    business: {
      monthly: 'https://buy.stripe.com/dRm28tdwidB64pK9sP57W05',
      yearly: 'https://buy.stripe.com/bJe6oJboagNibSc6gD57W06',
    },
    pro: {
      monthly: 'https://buy.stripe.com/5kQ28tak6cx26xS7kH57W07',
      yearly: 'https://buy.stripe.com/6oUeVf9g2dB6bSc20n57W08',
    },
  },

  // UAE (AED)
  ae: {
    starter: {
      monthly: 'https://buy.stripe.com/9B6bJ3gIu40wg8scF157W09',
      yearly: 'https://buy.stripe.com/cNifZjcsegNif4ofRd57W0a',
    },
    business: {
      monthly: 'https://buy.stripe.com/28EdRbgIudB69K46gD57W0b',
      yearly: 'https://buy.stripe.com/fZu7sNeAmaoU2hC48v57W0c',
    },
    pro: {
      monthly: 'https://buy.stripe.com/6oU14p9g2bsY1dy7kH57W0d',
      yearly: 'https://buy.stripe.com/cNi8wR2RE8gMf4oawT57W0e',
    },
  },

  // Saudi Arabia (SAR)
  sa: {
    starter: {
      monthly: 'https://buy.stripe.com/00wdRbeAm0OkcWg7kH57W0f',
      yearly: 'https://buy.stripe.com/dRm3cx0Jw8gM8G0bAX57W0g',
    },
    business: {
      monthly: 'https://buy.stripe.com/28E6oJ4ZMgNiaO87kH57W0h',
      yearly: 'https://buy.stripe.com/00w14pfEq1So9K448v57W0i',
    },
    pro: {
      monthly: 'https://buy.stripe.com/fZu4gB77U1So9K4bAX57W0j',
      yearly: 'https://buy.stripe.com/3cI8wRdwifJe1dyeN957W0k',
    },
  },
}
// ─────────────────────────────────────────────────────────────────────────────

export interface StripeCheckoutOptions {
  planId: string
  billingPeriod: 'monthly' | 'yearly'
  country: string
  email?: string
  name?: string
  phone?: string
}

export const stripeAPI = {
  getCheckoutUrl: (options: StripeCheckoutOptions): string => {
    const { planId, billingPeriod, country, email, name } = options

    // Get country-specific links, fallback to Egypt
    const countryCode = (country || 'eg').toLowerCase()
    const countryPlans =
      STRIPE_PAYMENT_LINKS[countryCode] || STRIPE_PAYMENT_LINKS['eg']

    const plan = countryPlans[planId as keyof StripeCountryPlans]
    if (!plan) {
      console.error('Unknown plan:', planId)
      return ''
    }

    const baseUrl = billingPeriod === 'monthly' ? plan.monthly : plan.yearly

    const params = new URLSearchParams()
    if (email) params.set('prefilled_email', email)
    if (name) params.set('prefilled_name', name)
    params.set('client_reference_id', email || 'unknown')

    const queryString = params.toString()
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  },

  redirectToCheckout: (options: StripeCheckoutOptions): void => {
    const url = stripeAPI.getCheckoutUrl(options)
    if (!url || url.includes('REPLACE_')) {
      console.error(
        '❌ Stripe Payment Link not configured for plan:',
        options.planId,
        'country:',
        options.country,
      )
      alert(
        'هذه الخطة غير متاحة للدفع الآن. تواصل معنا عبر واتساب.\nThis plan is not available yet. Contact us via WhatsApp.',
      )
      return
    }
    console.log('🔵 Redirecting to Stripe:', url)
    window.location.href = url
  },

  isConfigured: (
    planId: string,
    billingPeriod: 'monthly' | 'yearly',
    country?: string,
  ): boolean => {
    const countryCode = (country || 'eg').toLowerCase()
    const countryPlans = STRIPE_PAYMENT_LINKS[countryCode]
    if (!countryPlans) return false

    const plan = countryPlans[planId as keyof StripeCountryPlans]
    if (!plan) return false

    const url = billingPeriod === 'monthly' ? plan.monthly : plan.yearly
    return !!url && !url.includes('REPLACE_')
  },
}
